
import React, { useState } from 'react';
import { Mail, FileText, Globe } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AdminNewsletterTab from './AdminNewsletterTab';
import AdminContentTab from './AdminContentTab';
import AdminSitemapTab from './AdminSitemapTab';

interface AdminContentMarketingTabProps {
  subscribers: any[];
  isLoading: boolean;
  isProcessing: boolean;
  isSending: boolean;
  onToggleStatus: (subscriber: any) => void;
  onDeleteSubscriber: (subscriber: any) => void;
  onSendNewsletter: (subject: string, content: string) => Promise<boolean>;
  onRefresh: () => void;
  generatedContent: string;
  blogTitle: string;
  blogKeywords: string;
  onBlogGenerated: (content: string, topic: string, keywords: string) => void;
}

const AdminContentMarketingTab: React.FC<AdminContentMarketingTabProps> = ({
  subscribers,
  isLoading,
  isProcessing,
  isSending,
  onToggleStatus,
  onDeleteSubscriber,
  onSendNewsletter,
  onRefresh,
  generatedContent,
  blogTitle,
  blogKeywords,
  onBlogGenerated
}) => {
  const [activeSubTab, setActiveSubTab] = useState('newsletter');

  // Wrapper function to ensure proper return type
  const handleSendNewsletter = async (subject: string, content: string): Promise<boolean> => {
    try {
      await onSendNewsletter(subject, content);
      return true;
    } catch (error) {
      console.error('Error sending newsletter:', error);
      return false;
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="mb-6">
        <h2 className="text-xl font-semibold">Content & Marketing</h2>
        <p className="text-gray-600 mt-1">Manage newsletters, content generation, and sitemap</p>
      </div>

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-gray-100 mb-6">
          <TabsTrigger 
            value="newsletter" 
            className="flex items-center gap-2 data-[state=active]:bg-white"
          >
            <Mail className="h-4 w-4" />
            Newsletter
          </TabsTrigger>
          <TabsTrigger 
            value="content" 
            className="flex items-center gap-2 data-[state=active]:bg-white"
          >
            <FileText className="h-4 w-4" />
            Content Generator
          </TabsTrigger>
          <TabsTrigger 
            value="sitemap" 
            className="flex items-center gap-2 data-[state=active]:bg-white"
          >
            <Globe className="h-4 w-4" />
            Sitemap
          </TabsTrigger>
        </TabsList>

        <TabsContent value="newsletter">
          <AdminNewsletterTab
            subscribers={subscribers}
            isLoading={isLoading}
            isProcessing={isProcessing}
            isSending={isSending}
            onToggleStatus={onToggleStatus}
            onDeleteSubscriber={onDeleteSubscriber}
            onSendNewsletter={handleSendNewsletter}
            onRefresh={onRefresh}
          />
        </TabsContent>

        <TabsContent value="content">
          <AdminContentTab
            generatedContent={generatedContent}
            blogTitle={blogTitle}
            blogKeywords={blogKeywords}
            onBlogGenerated={onBlogGenerated}
          />
        </TabsContent>

        <TabsContent value="sitemap">
          <AdminSitemapTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminContentMarketingTab;
